import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonicModule, ModalController } from '@ionic/angular';
import { debounceTime } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { ConfirmPage } from 'src/pages/confirm/confirm.component';

import { Util } from 'src/shared/util/util';
import { readSecondaryPageContext } from 'src/shared/util/secondary-page-context';

import { AddressesQueriesService } from 'src/shared/services/addresses-queries.service';
import { BankRowsService, BankRowWithConnection } from 'src/shared/services/bank-rows.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { FinanceTypesService } from 'src/shared/services/finance-types.service';
import {
  FinancialTransactionRule,
  FinancialTransactionRulesService,
  RuleApplies,
  RuleClassification,
  RuleCondition,
  RuleTextOp,
} from 'src/shared/services/financial-transaction-rules.service';
import { PropertyTransactionsService } from 'src/shared/services/property-transactions.service';

import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

import { RadioButtonModel } from 'src/tidy-ui-components/components/form/radio-button/radio-button.model';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

interface RuleFormParams {
  rule?: FinancialTransactionRule | null;
  sample?: { description: string; transaction_type: 'Credit' | 'Debit' } | null;
  suggestedApplies?: RuleApplies | null;
}

type TextConditionGroup = FormGroup<{ op: FormControl<RuleTextOp>; value: FormControl<string> }>;

const MAX_PREVIEW_EXAMPLES = 5;

/**
 * Create or edit a classification rule. The form starts from a real bank
 * description whenever it can, and previews matches against the customer's
 * already-loaded bank rows so an overbroad "contains" is obvious before saving.
 */
@Component({
  templateUrl: 'transaction-rule-form.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TransactionRuleFormPage implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly modalCtrl = inject(ModalController);
  private readonly util = inject(Util);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly addressesQueries = inject(AddressesQueriesService);
  private readonly financeTypes = inject(FinanceTypesService);
  private readonly rulesService = inject(FinancialTransactionRulesService);
  private readonly bankRows = inject(BankRowsService);
  private readonly propertyTransactions = inject(PropertyTransactionsService);

  readonly operatorItems: SelectModel[] = [
    { value: 'contains', viewValue: 'contains' },
    { value: 'equals', viewValue: 'is exactly' },
    { value: 'matches', viewValue: 'matches pattern (regex)' },
  ];
  readonly transactionTypeItems: SelectModel[] = [
    { value: 'any', viewValue: 'Any type' },
    { value: 'Debit', viewValue: 'Debits only' },
    { value: 'Credit', viewValue: 'Credits only' },
  ];
  readonly classificationItems: RadioButtonModel[] = [
    { value: 'pnl', viewValue: 'Add to my P&L as…' },
    { value: 'skip', viewValue: 'Skip, not for my P&L' },
    { value: 'unknown', viewValue: 'Leave for me to review' },
  ];

  readonly form = this.fb.nonNullable.group({
    textConditions: this.fb.array<TextConditionGroup>([]),
    transactionType: ['any'],
    classification: ['pnl' as RuleClassification, Validators.required],
    monthly_finance_type_key: [''],
    address_id: [''],
  });

  isRightSideContent = true;
  loaded = false;
  submitted = false;
  saving = false;
  errorMessage = '';
  isEdit = false;
  rule: FinancialTransactionRule | null = null;
  pageTitle = 'New Rule';
  showSamplePicker = false;
  sampleItems: SelectModel[] = [];
  categoryItems: SelectModel[] = [];
  propertyItems: SelectModel[] = [];
  allBankRows: BankRowWithConnection[] = [];
  matchedRows: BankRowWithConnection[] = [];
  matchCount = 0;
  matchExamples: string[] = [];
  regexError = '';
  previewReady = false;

  readonly boundSave = () => this.save();

  get textConditions(): FormArray<TextConditionGroup> {
    return this.form.controls.textConditions;
  }

  get isPnl(): boolean {
    return this.form.controls.classification.value === 'pnl';
  }

  async ngOnInit() {
    const { isRightSideContent, params } = await readSecondaryPageContext<RuleFormParams>(this.storage);
    this.isRightSideContent = isRightSideContent;
    this.rule = params.rule ?? null;
    this.isEdit = !!this.rule;
    this.pageTitle = this.isEdit ? 'Edit Rule' : 'New Rule';
    this.rightSidePanelService.setDialogPageTitle(this.pageTitle);

    await this.loadPickers();
    if (this.isEdit) {
      this.prefillFromRule(this.rule);
    } else {
      this.prefillFromSample(params.sample ?? null, params.suggestedApplies ?? null);
      this.showSamplePicker = !params.sample;
    }
    this.form.valueChanges
      .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.computePreview());
    this.loaded = true;
    this.cdr.markForCheck();

    this.allBankRows = await this.bankRows.loadRecentBankRows();
    this.sampleItems = this.allBankRows.slice(0, 200).map(row => ({
      value: String(row.id),
      viewValue: row.description || 'Transaction',
      subtitle: `${row.date} · ${this.propertyTransactions.formatAmount(row)} · ${row.connectionLabel}`,
    }));
    this.previewReady = true;
    this.computePreview();
  }

  private async loadPickers(): Promise<void> {
    const [categories, addresses] = await Promise.all([
      this.financeTypes.getSelectItems().catch(() => [] as SelectModel[]),
      this.addressesQueries.getAddressesOnce().catch(() => []),
    ]);
    this.categoryItems = categories;
    this.propertyItems = [
      { value: '', viewValue: 'Decide per transaction' },
      ...addresses.map(address => ({
        value: String(address.id),
        viewValue: address['address_name'] || address.address1 || `Property #${address.id}`,
      })),
    ];
  }

  private prefillFromRule(rule: FinancialTransactionRule): void {
    const textConditions = rule.conditions.filter(c => c.type === 'text');
    const typeCondition = rule.conditions.find(c => c.type === 'transaction_type');
    for (const condition of textConditions) {
      this.textConditions.push(this.newTextCondition(condition.op as RuleTextOp, condition.value));
    }
    if (this.textConditions.length === 0) {
      this.textConditions.push(this.newTextCondition());
    }
    this.form.patchValue({
      transactionType: typeCondition ? typeCondition.value : 'any',
      classification: rule.applies.classification,
      monthly_finance_type_key: rule.applies.monthly_finance_type_key ?? '',
      address_id: rule.applies.address_id ? String(rule.applies.address_id) : '',
    });
  }

  private prefillFromSample(sample: RuleFormParams['sample'], suggested: RuleApplies | null): void {
    this.textConditions.push(this.newTextCondition('contains', sample?.description ?? ''));
    this.form.patchValue({
      classification: suggested?.classification ?? 'pnl',
      monthly_finance_type_key: suggested?.monthly_finance_type_key ?? '',
      address_id: suggested?.address_id ? String(suggested.address_id) : '',
    });
  }

  private newTextCondition(op: RuleTextOp = 'contains', value = ''): TextConditionGroup {
    return this.fb.nonNullable.group({
      op: [op],
      value: [value],
    });
  }

  addTextCondition(): void {
    this.textConditions.push(this.newTextCondition());
    this.cdr.markForCheck();
  }

  removeTextCondition(index: number): void {
    if (this.textConditions.length <= 1) {
      this.textConditions.at(0).controls.value.setValue('');
      return;
    }
    this.textConditions.removeAt(index);
    this.cdr.markForCheck();
  }

  /** The "Search recent transactions" picker next to a text field drops that row's description into it. */
  onSampleSelected(rowId: string, index = 0): void {
    const row = this.allBankRows.find(r => String(r.id) === String(rowId));
    const condition = this.textConditions.at(index) ?? this.textConditions.at(0);
    if (!row || !condition) { return; }
    condition.patchValue({ op: 'contains', value: row.description || '' });
    this.cdr.markForCheck();
  }

  buildConditions(): RuleCondition[] {
    const conditions: RuleCondition[] = this.textConditions.controls
      .map(group => group.getRawValue())
      .filter(value => value.value.trim().length > 0)
      .map(value => ({ type: 'text' as const, field: 'description' as const, op: value.op, value: value.value.trim() }));
    const transactionType = this.form.controls.transactionType.value;
    if (transactionType === 'Credit' || transactionType === 'Debit') {
      conditions.push({ type: 'transaction_type', op: 'equals', value: transactionType });
    }
    return conditions;
  }

  private buildApplies(): RuleApplies {
    const value = this.form.getRawValue();
    if (value.classification !== 'pnl') {
      return { classification: value.classification };
    }
    return {
      classification: 'pnl',
      ...(value.monthly_finance_type_key ? { monthly_finance_type_key: value.monthly_finance_type_key } : {}),
      ...(value.address_id ? { address_id: Number(value.address_id) } : {}),
    };
  }

  computePreview(): void {
    const conditions = this.buildConditions();
    this.regexError = '';
    for (const condition of conditions) {
      if (condition.type === 'text' && condition.op === 'matches' && !this.rulesService.isConditionComplete(condition)) {
        this.regexError = `"${condition.value}" is not a valid pattern.`;
      }
    }
    const hasText = conditions.some(c => c.type === 'text');
    if (!hasText || this.regexError || !this.previewReady) {
      this.matchedRows = [];
      this.matchCount = 0;
      this.matchExamples = [];
      this.cdr.markForCheck();
      return;
    }
    const datePipe = new ParseDateTimePipe();
    this.matchedRows = this.bankRows.filterMatching(this.allBankRows, conditions);
    this.matchCount = this.matchedRows.length;
    this.matchExamples = this.matchedRows.slice(0, MAX_PREVIEW_EXAMPLES).map(row =>
      `${datePipe.transform(row.date, 'MMM d, yyyy')} · ${row.description} · ${this.propertyTransactions.formatAmount(row)}`
    );
    this.cdr.markForCheck();
  }

  private validate(): string {
    const conditions = this.buildConditions();
    if (!conditions.some(c => c.type === 'text')) {
      return 'Enter at least one description to match.';
    }
    if (this.regexError) {
      return this.regexError;
    }
    if (this.isPnl && !this.form.controls.monthly_finance_type_key.value) {
      return 'Choose the category matching transactions should get.';
    }
    return '';
  }

  async save(): Promise<void> {
    this.submitted = true;
    if (this.saving) { return; }
    this.errorMessage = this.validate();
    if (this.errorMessage) {
      this.cdr.markForCheck();
      return;
    }
    if (this.demoBlock.block()) { return; }
    const body = { conditions: this.buildConditions(), applies: this.buildApplies() };
    this.saving = true;
    this.cdr.markForCheck();
    try {
      if (this.isEdit) {
        await this.rulesService.updateRule(this.rule.id, body);
        this.util.showSuccess('Rule updated. It applies to new transactions only.');
        this.goBack();
        return;
      }
      await this.rulesService.createRule(body);
      const applyCandidates = body.applies.classification === 'unknown' ? [] : this.matchedRows;
      if (applyCandidates.length === 0) {
        this.util.showSuccess('Rule saved. It applies to new transactions from now on.');
        this.goBack();
        return;
      }
      await this.askToApplyToExisting(body.applies, applyCandidates);
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Unable to save the rule.';
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  private async askToApplyToExisting(applies: RuleApplies, matched: BankRowWithConnection[]): Promise<void> {
    const count = matched.length;
    const noun = count === 1 ? 'transaction' : 'transactions';
    const modal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: {
        title: 'Rule saved',
        body: `Rules apply to new transactions only. Also apply this one to the ${count} past ${noun} it matches?`,
        backText: 'Only going forward',
        confirmText: `Apply to ${count} past`,
        confirmAction: async () => {
          try {
            const liveMap = await this.bankRows.loadLivePnlRowsByBankRowId();
            const fallbackAddressId = this.propertyItems.length === 2 ? Number(this.propertyItems[1].value) : null;
            const result = await this.bankRows.applyRuleToExistingRows(applies, matched, liveMap, fallbackAddressId);
            const parts: string[] = [];
            if (result.updated) { parts.push(`${result.updated} updated`); }
            if (result.added) { parts.push(`${result.added} added to the P&L`); }
            if (result.deleted) { parts.push(`${result.deleted} removed from the P&L`); }
            if (result.skippedMissingProperty) { parts.push(`${result.skippedMissingProperty} need a property first`); }
            if (result.failed) { parts.push(`${result.failed} failed`); }
            this.util.showSuccess(parts.length ? `Applied: ${parts.join(', ')}.` : 'Nothing to change on past transactions.');
            await this.storage.save('didChangeTransactions', true);
          } catch (err) {
            this.util.showError(err?.error?.message || err?.message || 'Unable to apply the rule to past transactions.');
          }
          modal.dismiss();
          this.goBack();
        },
        customBackAction: async () => {
          modal.dismiss();
          this.goBack();
        },
        confirmButtonClass: 'primary',
      },
      animated: false,
      cssClass: 'confirm-modal',
    });
    modal.present();
  }

  goBack(): void {
    if (this.isRightSideContent) {
      if (this.rightSidePanelService.historyContext$.getValue().length > 1) {
        this.rightSidePanelService.goBack();
      } else {
        this.rightSidePanelService.closeRightSidePanel();
      }
    } else {
      this.navCtrl.back();
    }
  }
}
